const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/polysports-api';
const ENABLE_CHINESE = import.meta.env.VITE_ENABLE_CHINESE === 'true';

// 获取认证头
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getLangParam = () => {
  const stored = localStorage.getItem('polysportslab-lang') || '';
  let normalized = stored.toLowerCase();
  if (!normalized) {
    const browserLang = navigator.language || navigator.userLanguage || '';
    normalized = browserLang.toLowerCase();
  }
  if (normalized.startsWith('zh')) return ENABLE_CHINESE ? 'zh' : 'en';
  if (normalized.startsWith('ja')) return 'ja';
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('en')) return 'en';
  return 'en';
};

const withLangQuery = (url) => {
  const lang = getLangParam();
  if (!lang) return url;
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}lang=${encodeURIComponent(lang)}`;
};

// 尝试刷新 token
const tryRefreshToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const data = await res.json();

    if (data.code === 'SUCCESS') {
      localStorage.setItem('access_token', data.data.access_token);
      localStorage.setItem('access_token_expire_at', data.data.access_token_expire_at);
      localStorage.setItem('refresh_token', data.data.refresh_token);
      localStorage.setItem('refresh_token_expire_at', data.data.refresh_token_expire_at);
      return true;
    }
  } catch {
    // 刷新失败
  }
  return false;
};

// 基础请求函数
async function fetchJson(url, options = {}) {
  const res = await fetch(url, options);
  const contentType = res.headers.get('content-type') || '';
  let data = null;
  let text = null;
  if (contentType.includes('application/json')) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  } else {
    try {
      text = await res.text();
    } catch {
      text = null;
    }
  }
  if (!res.ok) {
    const message = data?.msg || data?.detail || text || `API Error: ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data ?? text;
}

// 带认证的请求函数
// 未登录或 token 过期时自动跳转到登录页
async function fetchWithAuth(url, options = {}) {
  // 检查是否有 token
  const token = localStorage.getItem('access_token');
  if (!token) {
    // 自动跳转到登录页
    window.location.href = '/login';
    const err = new Error('请先登录');
    err.isAuthRequired = true;
    throw err;
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      ...getAuthHeaders(),
    },
  });

  // Token 过期，尝试刷新
  if (response.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          ...getAuthHeaders(),
        },
      }).then((r) => r.json());
    }
    // 刷新失败，跳转到登录页
    window.location.href = '/login';
    const err = new Error('登录已过期，请重新登录');
    err.isAuthRequired = true;
    throw err;
  }

  // 处理 403 会员权限不足
  if (response.status === 403) {
    const data = await response.json();
    const err = new Error(data.msg || '您的会员等级无权访问此功能，请升级会员');
    err.isMembershipRequired = true;
    err.currentTier = data.data?.current_tier;

    // 不自动触发弹窗，让调用者决定
    // 只有在没有被捕获的情况下才会到达全局错误处理
    throw err;
  }

  return response.json();
}

// 可选认证请求：有 token 则附带，无 token 则匿名访问
async function fetchWithOptionalAuth(url, options = {}) {
  const expireAt = localStorage.getItem('access_token_expire_at');
  if (expireAt && Date.now() >= parseInt(expireAt) * 1000) {
    await tryRefreshToken();
  }

  const headers = {
    ...options.headers,
    ...getAuthHeaders(),
  };

  const res = await fetch(url, {
    ...options,
    headers,
  });

  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export const api = {
  // ========== 认证 ==========
  // 创建待确认的登录凭证（新方式）
  createLoginTicket: (lang = null) =>
    fetchJson(`${BASE_URL}/auth/create-login-ticket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lang }),
    }),

  // 检查登录凭证状态（轮询用）
  checkLoginTicket: (ticketId) =>
    fetchJson(`${BASE_URL}/auth/check-login-ticket?ticket_id=${ticketId}`),

  verifyLogin: (ticketId, userId, code) =>
    fetchJson(`${BASE_URL}/auth/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket_id: ticketId, user_id: userId, code }),
    }),

  refreshToken: (refreshToken) =>
    fetchJson(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),

  requestEmailLoginCode: (email, lang = null) =>
    fetchJson(`${BASE_URL}/auth/email/request-login-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, lang }),
    }),

  verifyEmailLogin: (email, ticketId, code) =>
    fetchJson(`${BASE_URL}/auth/email/verify-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, ticket_id: ticketId, code }),
    }),

  getEmailStatus: () => fetchWithAuth(`${BASE_URL}/auth/email/status`),

  requestEmailBindCode: (email, lang = null) =>
    fetchWithAuth(`${BASE_URL}/auth/email/request-bind-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, lang }),
    }),

  bindEmail: (email, ticketId, code) =>
    fetchWithAuth(`${BASE_URL}/auth/email/bind`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, ticket_id: ticketId, code }),
    }),

  listUserApiKeys: () => fetchWithAuth(`${BASE_URL}/auth/api-keys`),

  createUserApiKey: (payload) =>
    fetchWithAuth(`${BASE_URL}/auth/api-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    }),

  revokeUserApiKey: (keyId, reason = null) =>
    fetchWithAuth(`${BASE_URL}/auth/api-keys/${keyId}/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reason ? { reason } : {}),
    }),

  rotateUserApiKey: (keyId) =>
    fetchWithAuth(`${BASE_URL}/auth/api-keys/${keyId}/rotate`, {
      method: 'POST',
    }),

  getSkillsTradingAuthorizationStatus: () =>
    fetchWithAuth(`${BASE_URL}/skills/v1/trading/authorization/status`),

  revokeSkillsTradingAuthorization: (grantId = null) =>
    fetchWithAuth(`${BASE_URL}/skills/v1/trading/authorization/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(grantId ? { grant_id: grantId } : {}),
    }),

  // ========== 会员 ==========
  getMe: () => fetchWithAuth(`${BASE_URL}/membership/me`),

  getMembershipTiers: () => fetchJson(`${BASE_URL}/membership/tiers`),

  // ========== 支付 ==========
  createCheckout: (tierName, periodMonths, payCurrency = null) =>
    fetchWithAuth(`${BASE_URL}/payment/create-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tier_name: tierName,
        period_months: periodMonths,
        pay_currency: payCurrency,
      }),
    }),

  getPaymentHistory: () => fetchWithAuth(`${BASE_URL}/payment/history`),

  getNowpaymentsOptions: () => fetchJson(`${BASE_URL}/payment/nowpayments/options`),

  getPaymentOrder: (orderId, options = {}) => {
    const query = options.refresh ? '?refresh=1' : '';
    return fetchWithAuth(`${BASE_URL}/payment/orders/${orderId}${query}`);
  },

  // ========== 代理 API ==========
  // 基础 API（需要认证）
  getMarkets: (limit = 60, sport = 'nba') =>
    fetchWithAuth(
      `${BASE_URL}/aggregate/market-list?limit=${limit}&sport=${encodeURIComponent(sport)}`
    ),

  getMarket: (id) => fetchWithAuth(`${BASE_URL}/proxy/polymarket?id=${id}`),

  findGame: (teamA, teamB, date) =>
    fetchWithAuth(
      `${BASE_URL}/proxy/find-game?teamA=${encodeURIComponent(teamA)}&teamB=${encodeURIComponent(teamB)}&date=${encodeURIComponent(date)}`
    ),

  getMarketDetailAggregate: (marketId, hours = 24) =>
    fetchWithAuth(
      withLangQuery(`${BASE_URL}/aggregate/market-detail/${marketId}?hours=${hours}`)
    ),

  // 高级 API（需要认证和会员权限）
  getPredictions: (marketIds) =>
    fetchWithAuth(withLangQuery(`${BASE_URL}/proxy/predictions/batch`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ marketIds }),
    }),

  getSmartMoney: () => fetchWithAuth(withLangQuery(`${BASE_URL}/proxy/smart-money`)),

  getSmartMoneyPositions: (slug, conditionId, teamA) =>
    fetchWithAuth(
      withLangQuery(
        `${BASE_URL}/proxy/smart-money/positions?slug=${slug}&conditionId=${conditionId}&teamA=${encodeURIComponent(teamA)}`
      )
    ),

  getMarketHolders: (conditionId, limit = 20) =>
    fetchWithAuth(
      `${BASE_URL}/proxy/market-holders?conditionId=${conditionId}&limit=${limit}`
    ),

  getOddsHistory: (marketId, hours, teamA, teamB, polyYes, polyNo) =>
    fetchWithAuth(
      `${BASE_URL}/proxy/odds/history?marketId=${marketId}&hours=${hours}&teamA=${encodeURIComponent(teamA)}&teamB=${encodeURIComponent(teamB)}&polyYes=${polyYes}&polyNo=${polyNo}`
    ),

  getOddsCompare: (marketId, teamA, teamB, polyYes, polyNo) =>
    fetchWithAuth(
      `${BASE_URL}/proxy/odds/compare?marketId=${marketId}&teamA=${encodeURIComponent(teamA)}&teamB=${encodeURIComponent(teamB)}&polyYes=${polyYes}&polyNo=${polyNo}`
    ),

  // Team Stats API (Free tier - for H2H and Form)
  getTeamStats: (team, days = 30) =>
    fetchJson(
      `${BASE_URL}/proxy/team-stats?team=${encodeURIComponent(team)}&days=${days}`
    ),

  getLineups: (gameId) =>
    fetchWithAuth(`${BASE_URL}/proxy/lineups?gameId=${gameId}`),

  getLiveScore: (eventId) =>
    fetchWithAuth(`${BASE_URL}/proxy/live-score?eventId=${eventId}`),

  getPlayerProps: (gameId, teamA, teamB, sport = 'nba', marketId = null) => {
    const marketParam = marketId ? `&marketId=${encodeURIComponent(marketId)}` : '';
    return fetchWithAuth(
      `${BASE_URL}/proxy/player-props?gameId=${gameId}&teamA=${encodeURIComponent(teamA || '')}&teamB=${encodeURIComponent(teamB || '')}&sport=${sport}${marketParam}`
    );
  },

  // Points API
  getPointsBalance: () => fetchWithAuth(`${BASE_URL}/points/balance`),
  getPointsProducts: () => fetchWithAuth(`${BASE_URL}/points/products`),
  getPointsLedger: (limit = 20, skip = 0, direction = null) => {
    const dirParam = direction ? `&direction=${encodeURIComponent(direction)}` : '';
    return fetchWithAuth(
      `${BASE_URL}/points/ledger?limit=${limit}&skip=${skip}${dirParam}`
    );
  },
  getPointsPurchase: (resourceType, resourceId, productCode = 'match_access') =>
    fetchWithAuth(
      `${BASE_URL}/points/purchases?resource_type=${encodeURIComponent(resourceType)}&resource_id=${encodeURIComponent(resourceId)}&product_code=${encodeURIComponent(productCode)}`
    ),
  createPointsPurchase: (productCode, resourceType, resourceId) =>
    fetchWithAuth(`${BASE_URL}/points/purchases`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_code: productCode,
        resource_type: resourceType,
        resource_id: resourceId,
      }),
    }),

  // Game Detail API (Free tier)
  getGameDetail: (eventId, teamA, teamB) =>
    fetchJson(
      `${BASE_URL}/proxy/game-detail?eventId=${eventId}&teamA=${encodeURIComponent(teamA)}&teamB=${encodeURIComponent(teamB)}`
    ),

  // Game Data API (Free tier - includes injuries)
  getGameData: (eventId, teamA, teamB, sport = 'nba') =>
    fetchJson(`${BASE_URL}/proxy/game-data?eventId=${eventId}&teamA=${encodeURIComponent(teamA || '')}&teamB=${encodeURIComponent(teamB || '')}&sport=${sport}`),

  // Game Lineups API (Free tier - real starting lineups from boxscore)
  // Note: Lineups are only available after game starts
  getGameLineups: (teamA, teamB, sport = 'nba', eventId = '') =>
    fetchJson(`${BASE_URL}/proxy/lineups?teamA=${encodeURIComponent(teamA || '')}&teamB=${encodeURIComponent(teamB || '')}&sport=${sport}&eventId=${eventId}`),

  // Hupu Play-by-Play API (Free tier)
  getHupuPlayByPlay: (teamA, teamB) =>
    fetchJson(
      `${BASE_URL}/proxy/hupu-playbyplay?teamA=${encodeURIComponent(teamA)}&teamB=${encodeURIComponent(teamB)}`
    ),

  // ESPN Play-by-Play API (Free tier)
  getEspnPlayByPlay: (eventId, sport = 'nba') =>
    fetchJson(
      `${BASE_URL}/proxy/espn-playbyplay?eventId=${eventId}&sport=${sport}`
    ),

  // Team Advanced Stats API (Pro tier)
  getTeamAdvancedStats: (teamId, marketId = null) => {
    const marketParam = marketId ? `&marketId=${encodeURIComponent(marketId)}` : '';
    return fetchWithAuth(
      `${BASE_URL}/proxy/team-advanced-stats?teamId=${teamId}${marketParam}`
    );
  },

  // ========== 钱包 ==========
  // 确保用户钱包存在
  ensureWallet: () =>
    fetchWithAuth(`${BASE_URL}/wallet/ensure`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    }),

  // 获取用户钱包列表
  getWallets: () => fetchWithAuth(`${BASE_URL}/wallet/list`),

  // 获取钱包余额
  getWalletBalance: (walletAddress) =>
    fetchWithAuth(`${BASE_URL}/wallet/${walletAddress}/balance`),

  // 设置交易钱包
  setTradingWallet: (walletAddress) =>
    fetchWithAuth(`${BASE_URL}/wallet/${walletAddress}/trading`, {
      method: 'PUT',
    }),

  // 导出私钥
  exportPrivateKey: (walletAddress) =>
    fetchWithAuth(`${BASE_URL}/wallet/${walletAddress}/export`),

  // 删除钱包
  deleteWallet: (walletAddress) =>
    fetchWithAuth(`${BASE_URL}/wallet/${walletAddress}`, {
      method: 'DELETE',
    }),

  // 获取白名单
  getWhitelist: () => fetchWithAuth(`${BASE_URL}/wallet/whitelist`),

  // 添加白名单
  addWhitelist: (address) =>
    fetchWithAuth(`${BASE_URL}/wallet/whitelist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ address }).toString(),
    }),

  // 移除白名单
  removeWhitelist: (address) =>
    fetchWithAuth(`${BASE_URL}/wallet/whitelist`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ address }).toString(),
    }),

  // 转账
  transfer: (walletAddress, toAddress, amount, token = 'USDC', asyncMode = true) =>
    fetchWithAuth(`${BASE_URL}/wallet/${walletAddress}/transfer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        to_address: toAddress,
        amount: amount.toString(),
        token,
        async_mode: asyncMode.toString(),
      }).toString(),
    }),

  // 查询转账任务状态
  getTransferJobStatus: (jobId) =>
    fetchWithAuth(`${BASE_URL}/wallet/transfer-jobs/${jobId}`),

  // ========== 交易 ==========
  getTradingAuthorizationStatus: () => fetchWithAuth(`${BASE_URL}/trading/authorization/status`),

  authorizeTrading: () =>
    fetchWithAuth(`${BASE_URL}/trading/authorization`, {
      method: 'POST',
    }),

  // 下单交易
  placeOrder: (tokenId, side, amount, price = null, extra = {}) =>
    fetchWithAuth(`${BASE_URL}/trading/order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token_id: tokenId,
        side,
        amount: amount.toString(),
        price: price?.toString() || null,
        ...extra,
      }),
    }),

  // 获取持仓
  getPositions: () => fetchWithAuth(`${BASE_URL}/trading/positions`),

  // 获取盈利汇总
  getProfitSummary: () => fetchWithAuth(`${BASE_URL}/trading/profit`),

  // 获取交易历史
  getTradeHistory: (limit = 50, offset = 0) =>
    fetchWithAuth(`${BASE_URL}/trading/history?limit=${limit}&offset=${offset}`),

  // 获取订单簿
  getOrderbook: (tokenId) =>
    fetchWithAuth(`${BASE_URL}/trading/orderbook/${tokenId}`),

  // 设置止盈止损（持仓）
  setAutoExit: (payload) =>
    fetchWithAuth(`${BASE_URL}/trading/auto-exit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    }),

  redeemPosition: (conditionId) =>
    fetchWithAuth(`${BASE_URL}/trading/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ condition_id: conditionId }),
    }),

  redeemAllPositions: () =>
    fetchWithAuth(`${BASE_URL}/trading/redeem-all`, {
      method: 'POST',
    }),

  // 获取市场 token IDs (by conditionId)
  getMarketTokens: (conditionId) =>
    fetchJson(`${BASE_URL}/trading/market-tokens/${conditionId}`),

  // 获取市场 token IDs (by slug - 更可靠)
  getMarketTokensBySlug: (slug) =>
    fetchJson(`${BASE_URL}/trading/market-tokens-by-slug/${slug}`),
};
